import { apiClient } from '../lib/apiClient';

/**
 * Two-factor authentication with an authenticator app (backend src/routes/meAccount.routes.ts,
 * src/services/accountMfa.service.ts — Cognito TOTP MFA):
 *   GET  /api/v1/me/mfa                                  → { data: MfaStatus }
 *   POST /api/v1/me/mfa/totp/setup                       → { data: TotpSetup }
 *   POST /api/v1/me/mfa/totp/verify  { code }            → { data: MfaStatus }
 *   POST /api/v1/me/mfa/totp/disable { password, code }  → { data: MfaStatus }
 * Errors: 400 CODE_MISMATCH, 403 WRONG_PASSWORD, 409 MFA_NOT_ENABLED,
 * 429 RATE_LIMITED, 502 MFA_CHANGE_FAILED, 503 MFA_UNAVAILABLE.
 */
export interface MfaStatus {
  enabled: boolean;
  available: boolean;
}

export interface TotpSetup {
  secret: string;
  otpauthUri: string;
}

export const MFA_STATUS_QUERY_KEY = ['me', 'mfa'] as const;

export async function fetchMfaStatus(): Promise<MfaStatus> {
  const res = await apiClient.get<{ data: MfaStatus }>('/api/v1/me/mfa');
  return res.data;
}

export async function startTotpSetup(): Promise<TotpSetup> {
  const res = await apiClient.post<{ data: TotpSetup }>('/api/v1/me/mfa/totp/setup');
  return res.data;
}

export async function confirmTotpSetup(code: string): Promise<MfaStatus> {
  const res = await apiClient.post<{ data: MfaStatus }>('/api/v1/me/mfa/totp/verify', { code });
  return res.data;
}

export async function disableTotp(password: string, code: string): Promise<MfaStatus> {
  const res = await apiClient.post<{ data: MfaStatus }>('/api/v1/me/mfa/totp/disable', { password, code });
  return res.data;
}

/** Groups the base32 secret in blocks of four for typing it into an app by hand. */
export function formatSecret(secret: string): string {
  return secret.replace(/(.{4})/g, '$1 ').trim();
}
